/**
 * DailyReportSuperadminPage.jsx — Superadmin campus daily report viewer
 *
 * - Shows a summary card per campus: submitted/not, yes/no counts, photo count
 * - Date picker to browse any historical date
 * - Clicking a campus card opens a detail modal with all answers + photo thumbnails
 * - Photos open in a full-screen lightbox
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  MdCheckCircle,
  MdRadioButtonUnchecked,
  MdCameraAlt,
  MdClose,
  MdRefresh,
  MdCalendarToday,
  MdFilterList,
} from "react-icons/md";
import { FiClipboard } from "react-icons/fi";
import { API_BASE_URL, getAuthHeaders } from "../../../utils/api";
import "./daily_report.css";

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtDateLong = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-PK", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });
};

// Build full API URL for a stored photo path
const photoApiUrl = (path) =>
  path ? `${API_BASE_URL}/api/daily-report/photo/${path.replace(/\\/g, "/")}` : null;

// Hook: fetch photo with auth headers → returns blob URL
function useAuthPhotoUrl(apiUrl) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!apiUrl) { setBlobUrl(null); return; }
    let cancelled = false;
    let current = null;
    (async () => {
      try {
        const res = await fetch(apiUrl, { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        current = url;
        setBlobUrl(url);
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [apiUrl]);
  return blobUrl;
}

// ── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  return (
    <div className="dr-lightbox-overlay" onClick={onClose}>
      <button className="dr-lightbox-close" onClick={onClose}><MdClose /></button>
      <img
        src={src}
        alt="Report photo"
        className="dr-lightbox-img"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── Answer chip ───────────────────────────────────────────────────────────────
function AnswerChip({ answer }) {
  const low = (answer || "").toLowerCase();
  const cls = low === "yes" ? "yes" : low === "no" ? "no" : low === "na" ? "na" : "text";
  const label = low === "na" ? "N/A" : answer || "—";
  return <span className={`dr-answer-chip ${cls}`}>{label}</span>;
}

// ── Single answer row in the detail modal — loads photo with auth ─────────────
function ReportAnswerRow({ answer, index }) {
  const [lightbox, setLightbox] = useState(null);
  const blobUrl = useAuthPhotoUrl(photoApiUrl(answer.photo_path));

  return (
    <div className="dr-answer-row-view">
      <div className="dr-view-num">{index + 1}</div>
      <div>
        <div className="dr-view-question-text">{answer.text}</div>
        <div className="dr-view-category">{answer.category}</div>
        {blobUrl && (
          <div className="dr-thumb-wrap" style={{ marginTop: ".45rem" }}>
            <img
              src={blobUrl}
              alt="Evidence"
              className="dr-thumb"
              onClick={() => setLightbox(blobUrl)}
              title="Click to enlarge"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
        {answer.photo_path && !blobUrl && (
          <div style={{ fontSize: ".74rem", color: "var(--dr-gray-400)", marginTop: ".35rem" }}>
            Loading photo…
          </div>
        )}
      </div>
      <div>
        <AnswerChip answer={answer.answer} />
        {answer.requires_photo && (
          <div style={{ marginTop: ".3rem" }}>
            {answer.photo_path
              ? <span className="dr-photo-done-chip"><MdCheckCircle size={11} /> Photo ✓</span>
              : <span className="dr-photo-badge"><MdCameraAlt size={11} /> Missing</span>
            }
          </div>
        )}
      </div>
      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}

// ── Report detail modal ───────────────────────────────────────────────────────
function ReportDetailModal({ stat, reportDate, onClose }) {
  const [report, setReport]     = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!stat.report_id) { setLoading(false); return; }
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/daily-report/${stat.report_id}`,
          { headers: getAuthHeaders() }
        );
        if (res.ok) setReport(await res.json());
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, [stat.report_id]);

  return (
    <div
      className="dr-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="dr-modal">
        <div className="dr-modal-header">
          <div>
            <h3 className="dr-modal-title">{stat.campus_name}</h3>
            <p className="dr-modal-sub">
              Daily Report · {fmtDateLong(reportDate)}
              {stat.submitted_by && (
                <> · Submitted by <strong>{stat.submitted_by}</strong></>
              )}
            </p>
          </div>
          <button className="dr-modal-close" onClick={onClose} aria-label="Close">
            <MdClose />
          </button>
        </div>

        {loading && <div className="dr-loading">Loading report…</div>}

        {!loading && !report && (
          <div className="dr-empty">
            <div className="dr-empty-icon"><FiClipboard /></div>
            <h3>Not Submitted</h3>
            <p>No daily report has been submitted for this campus today.</p>
          </div>
        )}

        {!loading && report && (
          <div className="dr-report-view">
            {/* Summary chips */}
            <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap", marginBottom: "1rem" }}>
              <span className="dr-answer-chip yes">✓ Yes: {stat.yes_count}</span>
              <span className="dr-answer-chip no">✗ No: {stat.no_count}</span>
              <span className="dr-answer-chip text">
                <MdCameraAlt size={11} style={{ marginRight: 3 }} />
                Photos: {stat.photo_count}
              </span>
              {report.is_complete ? (
                <span className="dr-answer-chip yes">✓ Complete</span>
              ) : (
                <span className="dr-answer-chip na">Incomplete</span>
              )}
            </div>

            {/* Each answer row loads its photo individually with auth */}
            {(report.answers || []).map((a, i) => (
              <ReportAnswerRow key={a.question_id} answer={a} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Campus summary card ───────────────────────────────────────────────────────
function CampusCard({ stat, onClick }) {
  const pct = stat.total_answers > 0
    ? Math.round((stat.yes_count / stat.total_answers) * 100)
    : 0;

  return (
    <div
      className="dr-campus-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
    >
      <div className="dr-campus-card-header">
        <div className="dr-campus-card-name">{stat.campus_name}</div>
        <div
          className={`dr-status-dot ${
            stat.is_complete ? "complete" : stat.submitted ? "submitted" : "missing"
          }`}
          title={stat.is_complete ? "Complete" : stat.submitted ? "Submitted (incomplete)" : "Not submitted"}
        />
      </div>

      {stat.submitted ? (
        <>
          <div className="dr-campus-card-meta" style={{ marginBottom: ".55rem" }}>
            <span>✓ {stat.yes_count} Yes</span>
            <span>✗ {stat.no_count} No</span>
            {stat.photo_count > 0 && (
              <span>📷 {stat.photo_count} Photos</span>
            )}
          </div>
          {stat.submitted_by && (
            <div style={{ fontSize: ".73rem", color: "var(--dr-gray-400)", marginBottom: ".5rem" }}>
              By: {stat.submitted_by}
            </div>
          )}
          <div className="dr-answer-bar-wrap">
            <div style={{
              display: "flex", justifyContent: "space-between",
              fontSize: ".7rem", color: "var(--dr-gray-400)", marginBottom: ".2rem",
            }}>
              <span>Yes rate</span><span>{pct}%</span>
            </div>
            <div className="dr-answer-bar">
              <div className="dr-answer-bar-fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </>
      ) : (
        <div style={{ fontSize: ".82rem", color: "#ef9a9a", fontWeight: 600 }}>
          <MdRadioButtonUnchecked size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />
          Not submitted yet
        </div>
      )}

      <div style={{
        marginTop: ".75rem", fontSize: ".74rem",
        color: "var(--dr-gray-400)", textAlign: "right",
      }}>
        Click to view details →
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DailyReportSuperadminPage({ showToast: extToast }) {
  const [reportDate, setReportDate]     = useState(todayISO());
  const [stats, setStats]               = useState(null);
  const [loading, setLoading]           = useState(true);
  const [selectedStat, setSelectedStat] = useState(null);
  const [filter, setFilter]             = useState("all"); // all | submitted | missing

  const [localToast, setLocalToast] = useState(null);
  const showToast = useCallback(
    (type, title, msg) => {
      if (extToast) { extToast(type, title, msg); return; }
      setLocalToast({ type, title, msg });
      setTimeout(() => setLocalToast(null), 3500);
    },
    [extToast]
  );

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/daily-report-stats?report_date=${reportDate}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error("Failed to load stats");
      setStats(await res.json());
    } catch (err) {
      showToast("error", "Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [reportDate, showToast]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const campuses = stats?.campuses || [];
  const submittedCount = campuses.filter((c) => c.submitted).length;
  const completeCount  = campuses.filter((c) => c.is_complete).length;
  const missingCount   = campuses.length - submittedCount;
  const totalPhotos    = campuses.reduce((s, c) => s + (c.photo_count || 0), 0);

  const filtered = campuses.filter((c) => {
    if (filter === "submitted") return c.submitted && c.is_complete;
    if (filter === "missing")   return !c.submitted;
    if (filter === "partial")   return c.submitted && !c.is_complete;
    return true;
  });

  return (
    <div className="dr-page-wide dashboard-content">

      {/* ── Header ── */}
      <div className="dr-page-header">
        <div className="dr-page-header-left">
          <div className="dr-page-icon"><FiClipboard size={22} /></div>
          <div>
            <h2 className="dr-page-title">Daily Campus Reports</h2>
            <p className="dr-page-sub">
              Review daily checklist submissions from all campuses.
            </p>
          </div>
        </div>
        <div className="dr-header-actions">
          <button className="dr-btn dr-btn-outline dr-btn-sm" onClick={fetchStats} disabled={loading}>
            <MdRefresh size={15} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      {stats && (
        <div className="dr-stat-row">
          <div className="dr-stat-card navy">
            <div className="dr-stat-label">Total Campuses</div>
            <div className="dr-stat-value">{campuses.length}</div>
          </div>
          <div className="dr-stat-card green">
            <div className="dr-stat-label">Submitted</div>
            <div className="dr-stat-value">{submittedCount}</div>
          </div>
          <div className="dr-stat-card red">
            <div className="dr-stat-label">Missing</div>
            <div className="dr-stat-value">{missingCount}</div>
          </div>
          <div className="dr-stat-card amber">
            <div className="dr-stat-label">Photos Uploaded</div>
            <div className="dr-stat-value">{totalPhotos}</div>
          </div>
        </div>
      )}

      {/* ── Toolbar ── */}
      <div className="dr-toolbar">
        <div className="dr-date-pill">
          <MdCalendarToday size={14} />
          <input
            type="date"
            value={reportDate}
            max={todayISO()}
            onChange={(e) => setReportDate(e.target.value)}
            className="dr-date-input"
            style={{ border: "none", background: "transparent", padding: 0, fontSize: ".82rem", cursor: "pointer" }}
          />
        </div>
        <div style={{ display: "flex", gap: ".35rem", flexWrap: "wrap" }}>
          {[
            { id: "all",       label: "All" },
            { id: "submitted", label: "Complete" },
            { id: "partial",   label: "Partial" },
            { id: "missing",   label: "Missing" },
          ].map((t) => (
            <button
              key={t.id}
              className={`dr-btn dr-btn-sm ${filter === t.id ? "dr-btn-primary" : "dr-btn-outline"}`}
              onClick={() => setFilter(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: ".82rem", color: "var(--dr-gray-600)", marginLeft: "auto" }}>
          {fmtDateLong(reportDate)}
        </div>
      </div>

      {/* ── Campus cards grid ── */}
      {loading ? (
        <div className="dr-loading">Loading campus reports…</div>
      ) : filtered.length === 0 ? (
        <div className="dr-empty">
          <div className="dr-empty-icon"><FiClipboard /></div>
          <h3>No campuses found</h3>
          <p>No campus reports match the selected filter.</p>
        </div>
      ) : (
        <div className="dr-overview-grid">
          {filtered.map((stat) => (
            <CampusCard
              key={stat.campus_id}
              stat={stat}
              onClick={() => setSelectedStat(stat)}
            />
          ))}
        </div>
      )}

      {/* ── Detail modal ── */}
      {selectedStat && (
        <ReportDetailModal
          stat={selectedStat}
          reportDate={reportDate}
          onClose={() => setSelectedStat(null)}
        />
      )}

      {/* Local toast */}
      {localToast && (
        <div
          className={`toast ${localToast.type}`}
          style={{ position: "fixed", bottom: "1.5rem", right: "1.5rem", zIndex: 9999, minWidth: 280 }}
        >
          <div style={{ padding: ".9rem 1.2rem" }}>
            <strong>{localToast.title}</strong>
            <p style={{ margin: ".2rem 0 0", fontSize: ".85rem" }}>{localToast.msg}</p>
          </div>
        </div>
      )}
    </div>
  );
}
